/**
 * Table Settings API Service
 * Manages column visibility and width settings via backend API
 */

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { API_BASE_URL } from '../helpers/common';

// Column settings (without order - order is in separate array)
export interface TableColumnSettings {
  [columnKey: string]: {
    visible: boolean;
    width?: number;
  };
}

// Complete table settings with columnOrder and sorting
export interface TableSettings {
  columnOrder: string[];
  columns: TableColumnSettings;
  sortField?: string;
  sortDirection?: 'ASC' | 'DESC';
}

// Request to save all table settings
export interface SaveTableSettingsRequest {
  tableId: string;
  columnOrder: string[];
  columns: TableColumnSettings;
  sortField?: string;
  sortDirection?: 'ASC' | 'DESC';
}

export const tableSettingsApi = createApi({
  reducerPath: 'tableSettingsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: API_BASE_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as any).user.info.accessToken;
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['TableSettings'],
  endpoints: (builder) => ({
    // Get table settings
    getTableSettings: builder.query<TableSettings | null, string>({
      query: (tableId) => `/settings/table/${tableId}`,
      transformResponse: (response: any): TableSettings | null => {
        // Handle wrapped format { data: {...} } from ResponseInterceptor
        if (response && typeof response === 'object' && 'data' in response) {
          // Return data even if null (no settings exist yet)
          return response.data;
        }
        // If response is already the settings object (not wrapped), return it directly
        if (response && typeof response === 'object' && !('status' in response)) {
          return response;
        }
        return null;
      },
      providesTags: (result, error, tableId) => [
        { type: 'TableSettings', id: tableId },
      ],
    }),

    // Save complete table settings (columnOrder + columns)
    saveTableSettings: builder.mutation<any, SaveTableSettingsRequest>({
      query: (data) => ({
        url: '/settings/table/save',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { tableId }) => [
        { type: 'TableSettings', id: tableId },
      ],
    }),

    // Reset table settings
    resetTableSettings: builder.mutation<any, string>({
      query: (tableId) => ({
        url: `/settings/table/${tableId}/reset`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, tableId) => [
        { type: 'TableSettings', id: tableId },
      ],
    }),

    // Get section UI settings
    getSectionSettings: builder.query<Record<string, any> | null, string>({
      query: (sectionId) => `/settings/section/${sectionId}`,
      transformResponse: (response: any): Record<string, any> | null => {
        if (response && typeof response === 'object' && 'data' in response)
          return response.data;
        return null;
      },
      providesTags: (result, error, sectionId) => [
        { type: 'TableSettings', id: `section_${sectionId}` },
      ],
    }),

    // Save section UI settings
    saveSectionSettings: builder.mutation<any, { sectionId: string; body: Record<string, any> }>({
      query: (data) => ({
        url: '/settings/section/save',
        method: 'POST',
        body: data,
      }),
    }),
  }),
});

export const {
  useGetTableSettingsQuery,
  useSaveTableSettingsMutation,
  useResetTableSettingsMutation,
  useGetSectionSettingsQuery,
  useSaveSectionSettingsMutation,
} = tableSettingsApi;

